import { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { api } from '../lib/api.js';
import { showAlert, showConfirm } from '../lib/tg.js';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemFooter,
  ItemGroup,
  ItemGroupContent,
  ItemGroupEmpty,
  ItemGroupHeader,
  ItemHeader,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { ErrorBanner } from '@/components/feedback';

interface Alert {
  id: string;
  kind: string;
  level: 'info' | 'warn' | 'error';
  payload: Record<string, unknown>;
  telegramMessageId?: number | null;
  createdAt: string;
  ackedAt?: string | null;
}

export function AlertsView() {
  const [items, setItems] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unackedOnly, setUnackedOnly] = useState(true);

  const reload = async () => {
    try {
      const qs = unackedOnly ? '?unacked=true' : '';
      setItems(await api.get<Alert[]>(`/api/alerts${qs}`));
    } catch (e) {
      setError((e as { message: string }).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    const t = setInterval(reload, 10000);
    return () => clearInterval(t);
  }, [unackedOnly]);

  const ack = async (id: string) => {
    try {
      await api.post(`/api/alerts/${id}/ack`);
      void reload();
    } catch (e) {
      void showAlert((e as { message: string }).message);
    }
  };

  const ackAll = async () => {
    if (!(await showConfirm('Acknowledge all unacked alerts?'))) return;
    try {
      await api.post('/api/alerts/ack-all');
      void reload();
    } catch (e) {
      void showAlert((e as { message: string }).message);
    }
  };

  return (
    <ItemGroup>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <ItemGroupHeader
        title="Alerts"
        action={
          <div className="flex items-center gap-3">
            <Label className="flex cursor-pointer items-center gap-2 text-xs font-normal text-muted-foreground">
              <Checkbox
                checked={unackedOnly}
                onCheckedChange={(v) => setUnackedOnly(v === true)}
              />
              unacked only
            </Label>
            <Button size="sm" variant="outline" onClick={ackAll}>
              Ack all
            </Button>
          </div>
        }
      />

      <ItemGroupContent>
        {loading && items.length === 0 && <ItemGroupEmpty>loading…</ItemGroupEmpty>}
        {!loading && items.length === 0 && <ItemGroupEmpty>no alerts</ItemGroupEmpty>}

        {items.map((a) => (
          <Item key={a.id}>
            <ItemMedia>
              <Bell />
            </ItemMedia>
            <ItemContent>
              <ItemHeader>
                <Badge variant={levelVariant(a.level)}>{a.level}</Badge>
                <ItemTitle>{a.kind}</ItemTitle>
              </ItemHeader>
              <pre className="mt-1 overflow-x-auto rounded border bg-muted p-2 text-[11px]">
                {JSON.stringify(a.payload, null, 2)}
              </pre>
              <ItemFooter>
                <span className="font-mono">{a.createdAt.slice(0, 19)}</span>
              </ItemFooter>
            </ItemContent>
            <ItemActions>
              {a.ackedAt ? (
                <span className="text-xs text-muted-foreground">acked</span>
              ) : (
                <Button variant="outline" size="sm" onClick={() => ack(a.id)}>
                  Ack
                </Button>
              )}
            </ItemActions>
          </Item>
        ))}
      </ItemGroupContent>
    </ItemGroup>
  );
}

function levelVariant(
  level: 'info' | 'warn' | 'error',
): 'destructive' | 'warning' | 'secondary' {
  if (level === 'error') return 'destructive';
  if (level === 'warn') return 'warning';
  return 'secondary';
}
